import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { IconButton, styled } from "@mui/material";
import UndoIcon from "@assets/icons/undoIcon.svg";
import RedoIcon from "@assets/icons/redoIcon.svg";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import CloseIcon from "@mui/icons-material/Close";
import { ModelMedia } from "@sportsgravyengineering/sg-api-react-sdk";
import { enqueueSnackbar } from "notistack";

window.Quill = Quill;
let quillObj: ReactQuill;

const StyledReactQuill = styled(ReactQuill)`
  height: 100% !important;

  .ql-container {
    top: 10px;
    border-top: 1px solid #c4c4c4 !important;
    border: 1px solid #c4c4c4;
    box-shadow: 0px 1px 2px 0px #0000001a;
    border-radius: 6px;
    padding: 14px 16px;
  }
  .ql-editor {
    padding: 0px;
    height: 30vh;
    cursor: text;
  }
`;

const StyledDiv = styled("div")`
  #toolbar .secondary-toolbar .ql-color-picker .ql-picker-options {
    color: #000;
    top: -110px;
    background: #fff;
    padding: 0;
    height: 106px;
  }
  #toolbar .ql-picker .ql-picker-options {
    color: #000;
    top: -110px;
    background: #fff;
    height: 106px;
  }
  #toolbar .ql-size .ql-picker-options {
    color: #000;
    top: -170px;
    background: #fff;
    height: 170px;
  }
`;

const StyledButtonContianer = styled("span")`
  border-right: 1px solid #e5e5e5;
`;

export const EmailReplyEditor = ({
  style,
  disabled,
  html,
  onValueChange,
  onBlur,
  files,
  setFiles
}: {
  style?;
  disabled?: boolean;
  html?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onValueChange?: (deltaOps: any[] | undefined, hasMedia: boolean) => void;
  onBlur?: () => void;
  onClose?: () => void;
  files?: { id: string; file: File | ModelMedia }[];
  setFiles?: (files: { id: string; file: File | ModelMedia }[]) => void;
  isSending?: boolean;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");

  const generateRandomString = () => {
    const array = new BigInt64Array(1);
    window.crypto.getRandomValues(array);
    return array[0].toString(36);
  };

  const handleAttachmentClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const onAddFile = (event: ChangeEvent<HTMLInputElement>) => {
    const tempFiles = (event.target as HTMLInputElement).files;
    if (tempFiles) {
      const filesArray = Array.from(tempFiles);
      const newFiles = filesArray.map((file) => {
        return { id: generateRandomString(), file };
      });
      if (setFiles && files) setFiles([...files, ...newFiles]);
    }
    event.target.value = "";
  };

  const removeFile = (id: string) => {
    if (files && setFiles) setFiles(files.filter((file) => file.id !== id));
  };

  const onChange = (e) => {
    const imgRegex = /<img.*?>/g;
    const hasMedia = imgRegex.test(e);
    setValue(e);
    if (onValueChange)
      onValueChange(quillObj.editor?.getContents().ops, hasMedia);
  };

  const modules = useMemo(() => {
    return {
      toolbar: {
        container: "#toolbar"
      },
      history: {
        delay: 1000,
        maxStack: 1000
      },
      clipboard: {
        matchVisual: false
      },
      ...(disabled
        ? {}
        : {
            imageResize: {
              parchment: Quill.import("parchment"),
              modules: ["Resize", "DisplaySize", "Toolbar"]
            }
          })
    };
  }, [disabled]);

  useEffect(() => {
    if (html) {
      setValue(html);
    }
  }, [html]);

  const downloadFile = async (media) => {
    if (media instanceof File) {
      const fileName = media.name || "download";
      const downloadUrl = URL.createObjectURL(media);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      return;
    }
    const url = media.baseUrl! + media.path;
    const fileName = media.metadata?.fileName || "download";
    if (media.mimeType === "application/pdf") {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName; // You can change the filename here
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        enqueueSnackbar("Error downloading the file");
      }
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const attachmentsList = () => {
    return (
      <div
        style={{
          marginTop: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          padding: "10px 0",
          maxHeight: "110px",
          overflow: "auto",
          borderTop: "1px solid #E5E5E5"
        }}
      >
        {files?.map((file) => {
          return (
            <div
              key={file.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                background: "#F3F4F7",
                padding: "10px",
                height: "37px",
                textOverflow: "ellipsis",
                borderRadius: "4px"
              }}
            >
              <div style={{ fontSize: "14px", fontWeight: 600 }}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadFile(file.file);
                  }}
                  style={{
                    color: "#007AFF",
                    cursor: "pointer",
                    textDecoration: "none"
                  }}
                >
                  {file.file instanceof File
                    ? file.file.name
                    : file.file.metadata?.fileName || file.id}
                </a>{" "}
                {file.file instanceof File && (
                  <span>
                    (
                    {file.file.size < 1024 * 1024
                      ? `${(file.file.size / 1024).toFixed(2)} KB`
                      : `${(file.file.size / (1024 * 1024)).toFixed(2)} MB`}
                    )
                  </span>
                )}
              </div>
              <IconButton
                onClick={() => removeFile(file.id)}
                disabled={disabled}
              >
                <CloseIcon
                  style={{
                    color: "#000",
                    height: "16px",
                    width: "16px",
                    cursor: "pointer"
                  }}
                />
              </IconButton>
            </div>
          );
        })}
      </div>
    );
  };

  const toolbar = () => {
    return (
      <div
        id="toolbar"
        style={{
          display: "flex",
          flexDirection: "column",
          border: "none",
          padding: 0
        }}
      >
        <div
          style={{
            height: "44px",
            display: "flex",
            alignItems: "center",
            backgroundColor: "#F3F4F7",
            boxShadow: "0px 1px 2px 0px #F3F4F7"
          }}
        >
          <StyledButtonContianer style={{ paddingRight: "5px" }}>
            <button
              className="ql-undo"
              style={{ paddingTop: "0" }}
              onClick={() => quillObj.getEditor().history.undo()}
            >
              <img src={UndoIcon} />
            </button>
            <button
              className="ql-redo"
              style={{ paddingTop: "0" }}
              onClick={() => quillObj.getEditor().history.redo()}
            >
              <img src={RedoIcon} />
            </button>
          </StyledButtonContianer>
          <StyledButtonContianer>
            <select className="ql-font"></select>
          </StyledButtonContianer>
          <StyledButtonContianer>
            <select className="ql-size"></select>
          </StyledButtonContianer>
          <StyledButtonContianer>
            <button className="ql-bold"></button>
            <button className="ql-italic"></button>
            <button className="ql-underline"></button>
            <select className="ql-color"></select>
          </StyledButtonContianer>
          <StyledButtonContianer>
            <select className="ql-align"></select>
            <button className="ql-list" value="ordered"></button>
            <button className="ql-list" value="bullet"></button>
            <button className="ql-indent" value="-1"></button>
            <button className="ql-indent" value="+1"></button>
          </StyledButtonContianer>
          <button className="ql-blockquote"></button>
          <button className="ql-strike"></button>
          <button className="ql-attachment" onClick={handleAttachmentClick}>
            <AttachFileRoundedIcon />
            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={onAddFile}
            />
          </button>
          <select className="ql-background"></select>
          <button className="ql-link"></button>
          <button className="ql-code-block"></button>
        </div>
      </div>
    );
  };

  return (
    <StyledDiv>
      {toolbar()}
      <StyledReactQuill
        ref={(el) => {
          quillObj = el;
        }}
        value={value}
        readOnly={disabled}
        style={style ? style : {}}
        onChange={onChange}
        onBlur={() => {
          if (onBlur) onBlur();
        }}
        modules={modules}
      ></StyledReactQuill>
      {files && files.length > 0 && attachmentsList()}
    </StyledDiv>
  );
};
